import { useEffect, useMemo } from "react";
import { useGetAssessmentTemplates, useGetAssessmentVariableCollection, useGetDiscoverSubject, } from "@piiqtechnologies/ui-lib";
import { newTemplateRunAt, subtopicsBannerState } from "@utils/subtopicsBanner";
import { latestReportId } from "./useSelectedReport";
/**
 * Whether the header should say anything about subtopic edits, and which thing.
 *
 * The dates it needs live in two records the report list does not carry: the template knows when
 * it last ran and when it runs next, and the variable collection knows when its subtopics were
 * last saved. Both are read for the template behind the report on screen, so switching reports
 * answers for that report rather than for the community's newest one.
 */
export const useSubtopicsBanner = ({ communityId, reports, selectedReportId, }) => {
    var _a;
    const [fetchTemplates, templates] = useGetAssessmentTemplates();
    const [fetchCollection, collection] = useGetAssessmentVariableCollection();
    const [fetchSubject, subject] = useGetDiscoverSubject();
    useEffect(() => {
        if (!communityId)
            return;
        fetchTemplates({ subjectId: communityId });
        fetchSubject(communityId);
    }, [communityId, fetchTemplates, fetchSubject]);
    const autoReportEnabled = (subject === null || subject === void 0 ? void 0 : subject._id) === communityId ? subject === null || subject === void 0 ? void 0 : subject.autoReportEnabled : undefined;
    const selectedTemplateId = (_a = reports.find((report) => report.reportId === selectedReportId)) === null || _a === void 0 ? void 0 : _a.templateId;
    const template = useMemo(() => {
        if (!(templates === null || templates === void 0 ? void 0 : templates.length))
            return undefined;
        const byReport = selectedTemplateId
            ? templates.find((candidate) => candidate._id === selectedTemplateId)
            : undefined;
        if (byReport)
            return byReport;
        // A report whose template is gone, or a community with no report yet: the newest template is
        // the one whose schedule and subtopics the next run will use.
        return [...templates].sort((a, b) => { var _a, _b, _c, _d; return new Date((_b = (_a = b.updatedAt) !== null && _a !== void 0 ? _a : b.createdAt) !== null && _b !== void 0 ? _b : 0).getTime() - new Date((_d = (_c = a.updatedAt) !== null && _c !== void 0 ? _c : a.createdAt) !== null && _d !== void 0 ? _d : 0).getTime(); })[0];
    }, [templates, selectedTemplateId]);
    const newTemplateNextRunAt = useMemo(() => newTemplateRunAt(templates), [templates]);
    const collectionId = template === null || template === void 0 ? void 0 : template.assessmentVariableCollectionId;
    useEffect(() => {
        if (collectionId)
            fetchCollection(collectionId);
    }, [collectionId, fetchCollection]);
    // The hook holds the last collection it fetched, so a template with no collection — or a moment
    // where none is resolved — would otherwise leave the previous report's dates in place and put a
    // banner on a report they do not describe.
    const currentCollection = (collection === null || collection === void 0 ? void 0 : collection._id) === collectionId ? collection : undefined;
    const isMostRecentReport = !selectedReportId || selectedReportId === latestReportId(reports);
    return useMemo(() => isMostRecentReport
        ? subtopicsBannerState({
            nextRunAt: template === null || template === void 0 ? void 0 : template.nextReportAt,
            subtopicsUpdatedAt: currentCollection === null || currentCollection === void 0 ? void 0 : currentCollection.subtopicsUpdatedAt,
            templateId: template === null || template === void 0 ? void 0 : template._id,
            templateCreatedAt: template === null || template === void 0 ? void 0 : template.createdAt,
            lastRunAt: template === null || template === void 0 ? void 0 : template.lastAssessmentAt,
            templateActive: template === null || template === void 0 ? void 0 : template.isActive,
            autoReportEnabled,
            newTemplateNextRunAt,
            reports,
            selectedReportId,
        })
        : undefined, [
        isMostRecentReport,
        template === null || template === void 0 ? void 0 : template.nextReportAt,
        template === null || template === void 0 ? void 0 : template.lastAssessmentAt,
        template === null || template === void 0 ? void 0 : template.isActive,
        autoReportEnabled,
        currentCollection === null || currentCollection === void 0 ? void 0 : currentCollection.subtopicsUpdatedAt,
        template === null || template === void 0 ? void 0 : template._id,
        template === null || template === void 0 ? void 0 : template.createdAt,
        newTemplateNextRunAt,
        reports,
        selectedReportId,
    ]);
};
